import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useNavigate } from "react-router-dom";
import { useClientInfo } from "../utils/commonImports";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawClientHeader } from "../utils/reportClientHeader";
import { addPdfFooter } from "../utils/reportFooter";

export default function CustomersReport() {
  const navigate = useNavigate();
  const { clientId, client } = useClientInfo();

  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  const printStyles = `
@media print {

  @page {
    size: A4 landscape;
    margin: 10mm;
  }

  body {
    background: white;
    -webkit-print-color-adjust: exact;
  }

  /* Hide everything */
  body * {
    visibility: hidden;
  }

  /* Show only report */
  .print-report,
  .print-report * {
    visibility: visible;
  }

  .print-report {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }

  /* Hide back button / breadcrumb when printing */
  .no-print {
    display: none !important;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th, td {
    border: 1px solid #000;
    font-size: 11px;
    padding: 4px;
    color: #000;
  }

}
`;

  // -----------------------
  // Back button
  // Goes to the previous page. If there is no history
  // (page opened directly), goes to the Reports dashboard.
  // -----------------------
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/reports");
    }
  };

  // -----------------------
  // Load Report
  // -----------------------
  const loadReport = async () => {
    const res = await axios.get("/api/customers/report", {
      params: {
        client_id: clientId,
        search,
        type,
        status,
      },
    });

    setRows(res.data || []);
  };

  const handlePrint = () => {
    const doc = generateCustomerPDF();

    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;

    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow.print();
    };
  };

  const handleRealPDF = () => {
    const doc = generateCustomerPDF();
    doc.save("Customers_Report.pdf");
  };

  const generateCustomerPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    // ============================
    // CLIENT HEADER (LOGO + DETAILS)
    // ============================

    const headerEndY = drawClientHeader(doc, client);
    // client = your client object

    // ============================
    // REPORT TITLE
    // ============================

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text("List of Customers", 14, headerEndY);

    // ============================
    // TABLE COLUMNS
    // ============================

    const columns = [
      "Customer Name",
      "Type",
      "Contact Person",
      "Mobile",
      "Email",
      "GSTIN",
      "City",
      "State",
      "Status",
      "Opening Balance",
    ];

    // ============================
    // TABLE BODY
    // ============================

    const body = rows.map((r) => [
      r.customername,
      r.customertype,
      r.customerprimarycontactname,
      r.customerprimarycontactphone,
      r.customerprimarycontactemailaddress,
      r.customergstin,
      r.customerbillingcity,
      r.customerbillingstate,
      r.customeractivestatus,
      Number(r.customeropeningbalance || 0).toFixed(2),
    ]);

    // ============================
    // TABLE
    // ============================

    autoTable(doc, {
      head: [columns],
      body,
      startY: headerEndY + 6,
      theme: "grid",

      tableWidth: "auto",

      styles: {
        fontSize: 9,
        cellPadding: 3,
      },

      headStyles: {
        fillColor: [230, 230, 230], // Light Grey
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
    });

    // =========================
    // FOOTER (ALWAYS LAST ❗)
    // =========================
    addPdfFooter(doc);
    return doc;
  };

  const handleExcel = () => {
    const data = rows.map((r) => ({
      "Customer Name": r.customername,
      Type: r.customertype,
      "Contact Person": r.customerprimarycontactname,
      Mobile: r.customerprimarycontactphone,
      Email: r.customerprimarycontactemailaddress,
      GSTIN: r.customergstin,
      City: r.customerbillingcity,
      State: r.customerbillingstate,
      Status: r.customeractivestatus,
      "Opening Balance": Number(r.customeropeningbalance || 0).toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");

    XLSX.writeFile(wb, "Customers_Report.xlsx");
  };

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setOrder(newOrder);
    setOrderBy(field);

    const sorted = [...rows].sort((a, b) => {
      if ((a[field] || "") < (b[field] || ""))
        return newOrder === "asc" ? -1 : 1;
      if ((a[field] || "") > (b[field] || ""))
        return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setRows(sorted);
  };

  useEffect(() => {
    if (clientId) loadReport();
  }, [clientId]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = printStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // -----------------------
  return (
    <Paper className="print-area print-report" sx={{ p: 3 }}>
      {/* HEADER WITH BACK BUTTON */}
      <Box className="no-print" display="flex" alignItems="center" gap={1.5} mb={2}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate("/reports")}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Customers
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            List of Customers
          </Typography>
        </Box>
      </Box>

      {/* FILTERS */}
      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
        <TextField
          size="small"
          label="Search Name / Mobile / GSTIN"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{ sx: { fontSize: 12 } }}
          InputLabelProps={{ sx: { fontSize: 12 } }}
        />

        <TextField
          size="small"
          select
          label="Customer Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{ minWidth: 160 }}
          InputProps={{ sx: { fontSize: 12 } }}
          InputLabelProps={{ sx: { fontSize: 12 } }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Business">Business</MenuItem>
          <MenuItem value="Individual">Individual</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 130 }}
          InputProps={{ sx: { fontSize: 12 } }}
          InputLabelProps={{ sx: { fontSize: 12 } }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </TextField>

        <Button
          variant="contained"
          size="small"
          sx={{ fontSize: 12, height: 36 }}
          onClick={loadReport}
        >
          Search
        </Button>

        <Button variant="outlined" onClick={handlePrint}>
          Print
        </Button>

        <Button variant="outlined" color="error" onClick={handleRealPDF}>
          PDF
        </Button>

        <Button variant="outlined" color="success" onClick={handleExcel}>
          Excel
        </Button>
      </Box>

      {/* TABLE */}
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead sx={{ background: "#f1f5f9" }}>
          <TableRow>
            <TableCell
              sx={{
                width: 200,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customername")}
            >
              Customer Name
            </TableCell>

            <TableCell
              sx={{
                width: 70,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customertype")}
            >
              Type
            </TableCell>

            <TableCell
              sx={{
                width: 120,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customerprimarycontactname")}
            >
              Contact Person
            </TableCell>

            <TableCell
              sx={{
                width: 100,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customerprimarycontactphone")}
            >
              Mobile
            </TableCell>

            <TableCell
              sx={{
                width: 180,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customerprimarycontactemailaddress")}
            >
              Email
            </TableCell>

            <TableCell
              sx={{
                width: 110,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customergstin")}
            >
              GSTIN
            </TableCell>

            <TableCell
              sx={{
                width: 90,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customerbillingcity")}
            >
              City
            </TableCell>

            <TableCell
              sx={{
                width: 90,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customerbillingstate")}
            >
              State
            </TableCell>

            <TableCell
              sx={{
                width: 70,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => handleSort("customeractivestatus")}
            >
              Status
            </TableCell>

            <TableCell
              sx={{
                width: 120,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              align="right"
              onClick={() => handleSort("customeropeningbalance")}
            >
              Opening Balance
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.customerid}>
              <TableCell sx={{ width: 200, fontSize: 12 }}>
                {r.customername}
              </TableCell>
              <TableCell sx={{ width: 70, fontSize: 12 }}>
                {r.customertype}
              </TableCell>
              <TableCell sx={{ width: 120, fontSize: 12 }}>
                {r.customerprimarycontactname}
              </TableCell>
              <TableCell sx={{ width: 100, fontSize: 12 }}>
                {r.customerprimarycontactphone}
              </TableCell>
              <TableCell sx={{ width: 180, fontSize: 12 }}>
                {r.customerprimarycontactemailaddress}
              </TableCell>
              <TableCell sx={{ width: 110, fontSize: 12 }}>
                {r.customergstin}
              </TableCell>
              <TableCell sx={{ width: 90, fontSize: 12 }}>
                {r.customerbillingcity}
              </TableCell>
              <TableCell sx={{ width: 90, fontSize: 12 }}>
                {r.customerbillingstate}
              </TableCell>
              <TableCell sx={{ width: 70, fontSize: 12 }}>
                {r.customeractivestatus}
              </TableCell>
              <TableCell sx={{ width: 120, fontSize: 12 }} align="right">
                {Number(r.customeropeningbalance || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ fontSize: 12 }}>
                No records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
